import type { StatusTone } from "@fawkes/ui";

/** Klantverzoeken uit het klantportaal (api: /api/klantverzoeken). Zie ontwerp Klantportaal Pension, hoofdstuk 10 en 21. */

export type VerzoekStatus =
  | "NIEUW"
  | "IN_BEHANDELING"
  | "AANVULLING_NODIG"
  | "VOORSTEL_KLANT"
  | "GOEDGEKEURD"
  | "VERWERKT"
  | "MISLUKT"
  | "AFGEWEZEN"
  | "INGETROKKEN"
  | "VERLOPEN";

export type Actie = "CLAIMEN" | "AANVULLING" | "AFWIJZEN" | "GOEDKEUREN" | "VOORSTEL" | "OPNIEUW";

export interface Regel {
  id: string;
  nummer: string;
  status: VerzoekStatus;
  type: string;
  klantId: number | null;
  klantnaam: string | null;
  huishouden: string | null;
  nieuweKlant: boolean;
  kanaal: string;
  ingediend: string;
  geclaimdDoor: string | null;
  teLangNieuw: boolean;
  /** Wat er gevraagd wordt, in een regel (periode en dieren, de naam van een dier, of de gewijzigde velden). */
  samenvatting?: string | null;
  /** Wat er gewijzigd is (oud → nieuw), of bij iets nieuws wat de klant invulde (oud leeg). */
  velden?: Aanpassing[];
}

export interface Aanpassing {
  naam: string;
  oud: string | null;
  nieuw: string | null;
}

export interface Telling {
  open: number;
  registraties: number;
  mislukt: number;
  teLangNieuw: number;
}

export type Inhoud = Record<string, unknown>;

export interface Driewegvergelijking {
  oud: Inhoud | null;
  huidig: Inhoud | null;
  gevraagd: Inhoud | null;
}

export interface Versie {
  versie: number;
  payloadJson: string;
  aanleiding: string;
  voorstelId: string | null;
  accountId: string;
  op: string;
}

export interface Voorstel {
  id: string;
  versie: number;
  payloadJson: string;
  prijsJson: string | null;
  reden: string;
  bijAfwijzen: "TERUG_NAAR_BEOORDELING" | "AFWIJZEN";
  afwijsReden: string | null;
  expiresAt: string;
  status: "OPEN" | "GEACCEPTEERD" | "AFGEWEZEN" | "VERLOPEN" | "VERVALLEN";
  voorgesteldDoor: string;
  voorgesteldOp: string;
}

export interface Historie {
  id: string;
  van: VerzoekStatus | null;
  naar: VerzoekStatus;
  door: "KLANT" | "MEDEWERKER" | "SYSTEEM";
  doorMedewerker: string | null;
  versie: number | null;
  klantTekst: string | null;
  interneNotitie: string | null;
  op: string;
}

export interface Detail {
  id: string;
  nummer: string;
  status: VerzoekStatus;
  misluktReden: string | null;
  type: string;
  klantId: number | null;
  klantnaam: string | null;
  acties: Actie[];
  huishouden: string | null;
  indiener: string | null;
  kanaal: string;
  doelType: string | null;
  doelId: string | null;
  versie: number;
  inhoud: Inhoud;
  momentopname: Inhoud | null;
  reactieKlant: string | null;
  notitieIntern: string | null;
  geclaimdDoor: string | null;
  behandeldDoor: string | null;
  ingediend: string;
  aanvullenVoor: string | null;
  overlappend: string[];
  conflict: Driewegvergelijking | null;
  versies: Versie[];
  voorstellen: Voorstel[];
  historie: Historie[];
  gekoppeldAan: string | null;
}

export interface MatchVeld {
  veld: string;
  registratie: string | null;
  klant: string | null;
  gelijk: boolean;
}

export interface Match {
  klantId: number;
  naam: string | null;
  actief: boolean;
  redenen: ("EMAIL" | "TELEFOON" | "ADRES")[];
  vergelijking: MatchVeld[];
}

/** Kleur van een status; MISLUKT is rood omdat de medewerker er iets mee moet. */
export function toon(status: VerzoekStatus): StatusTone {
  switch (status) {
    case "NIEUW":
      return "info";
    case "IN_BEHANDELING":
    case "AANVULLING_NODIG":
    case "VOORSTEL_KLANT":
      return "warning";
    case "GOEDGEKEURD":
    case "VERWERKT":
      return "success";
    case "MISLUKT":
    case "AFGEWEZEN":
      return "danger";
    default:
      return "neutral";
  }
}

/** De foutcode en details uit een mislukte api-aanroep (dataProvider zet de body op de fout). */
export function foutBody(err: unknown): { code?: string; melding?: string; details?: unknown } {
  const body = (err as { body?: unknown } | null)?.body;
  return body && typeof body === "object" ? (body as { code?: string; melding?: string; details?: unknown }) : {};
}

export function leesJson(waarde: string | null | undefined): Inhoud | null {
  if (!waarde) {
    return null;
  }
  try {
    return JSON.parse(waarde) as Inhoud;
  } catch {
    return null;
  }
}

/** Een waarde uit een verzoek leesbaar maken: tekst blijft tekst, de rest wordt compacte JSON. */
export function waardeTekst(waarde: unknown): string {
  if (waarde == null) {
    return "";
  }
  return typeof waarde === "object" ? JSON.stringify(waarde) : String(waarde);
}

/** GET /api/klantverzoeken/{id}/reservering: een RESERVERING_NIEUW zoals hij nu in de backoffice zou landen. */
export interface ReserveringAanvraag {
  aankomst: string | null;
  aankomstTijd: string | null;
  vertrek: string | null;
  vertrekTijd: string | null;
  vestiging: string | null;
  wizardVersie: number;
  dieren: {
    dierId: number;
    naam: string;
    soort: string | null;
    accommodatie: string | null;
    voeding: string | null;
    voedingInstructies: string | null;
    medicatie: boolean;
    medicatieInstructies: string | null;
    samenMetGezin: boolean;
    bijzonderheden: string | null;
    extras: { arrangementId: number; naam: string; aantal: number }[];
  }[];
  extras: { arrangementId: number; naam: string; aantal: number }[];
  annuleringsverzekering: boolean;
  overigeWensen: string | null;
  antwoorden: { vraag: string; dier: string | null; waarde: string | null }[];
  prijs: Prijscontrole | null;
  beschikbaarheid: {
    beschikbaar: boolean;
    capaciteitIngesteld: boolean;
    knelpunten: { datum: string; diersoort: string; reden: "VOL" | "CAPACITEIT"; vrij: number | null }[];
  } | null;
}

/** Wat de klant zag tegenover wat het nu kost; boven de drempel eerst een tegenvoorstel. */
export interface Prijscontrole {
  gezien: number | null;
  bron: "AANVRAAG" | "VOORSTEL" | null;
  nu: number | null;
  verschil: number | null;
  toegestaan: number | null;
  akkoordNodig: boolean;
  opbouw: { totaal: number; regels: { omschrijving: string; berekening: string | null; bedrag: number }[] } | null;
  fout: string | null;
}
